import { useEffect, useState } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";

export default function Subscribers() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

  const { showModal } = useMessageModal();
  const { formatDateToDMY } = useFormat();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  const fetchSubscribers = () => {
    fetch(`${BASE_URL}/admin/subscribers`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((res) => setData(res.data));
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  // =========================
  // UPDATE STATUS ONLY
  // =========================
  const handleUpdate = async (formData: any) => {
    try {
      const res = await fetch(`${BASE_URL}/admin/subscriber/${activeRow.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: formData.status === "active" ? "active" : "inactive",
        }),
      });

      const result = await res.json();

      if (result.success) {
        fetchSubscribers();
        setModalOpen(false);

        showModal({
          type: "success",
          title: "Updated",
          message: "Subscriber status updated successfully.",
          confirmText: "OK",
        });
      }
    } catch (error) {
      showModal({
        type: "error",
        title: "Error",
        message: "Failed to update subscriber.",
      });
    }
  };

  // =========================
  // DELETE
  // =========================
  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Subscriber",
      message: "Are you sure you want to delete this subscriber?",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/subscriber/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchSubscribers();

            showModal({
              type: "success",
              title: "Deleted",
              message: "Subscriber deleted successfully.",
            });
          }
        } catch {
          showModal({
            type: "error",
            title: "Error",
            message: "Failed to delete subscriber.",
          });
        }
      },
    });
  };

  const subscriberFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },

    { key: "name", label: "Name", colSpan: 2 },

    {
      key: "image",
      label: "Profile Image",
      type: "image",
      imageType: "single",
      imageFolder: "subscribers",
      disabled: true,
      colSpan: 2,
    },

    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  const columns: Column[] = [
    { key: "id", label: "ID" },

    { key: "image", label: "Image", imageFolder: "subscribers" },

    { key: "name", label: "Name" },

    { key: "company_name", label: "Company" },

    { key: "email", label: "Email" },

    { key: "phone", label: "Phone" },

    { key: "city", label: "City" },

    { key: "country", label: "Country" },

    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },

    {
      key: "subscribed_at",
      label: "Subscribed",
      render: (row: any) => formatDateToDMY(row.subscribed_at),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Subscribers"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="subscribers"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Subscriber"
        fields={subscriberFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleUpdate}
      />
    </div>
  );
}
